import queries from '@apis/queries';
import Column from '@components/Column';
import Row from '@components/Row';
import Text from '@components/Text';
import colors from '@constants/colors';
import styled from '@emotion/styled';
import RefreshIcon from '@icons/Refresh';
import { OrderStatus } from '@models/order/orderLog';
import { OrderStep } from '@models/order/orderLog/extension';
import { pressActionEffect } from '@styles/mixin';
import { useQueryClient } from '@tanstack/react-query';
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Dot from './Dot';
import styles from './styles.module.scss';

interface Props {
    orderStatus: OrderStatus
}

function OrderProgress({ orderStatus }:Props) {
    const { t } = useTranslation('orderLog');
    const step = OrderStep[orderStatus];
    const progressWidth = (step / 3) * 100;
    const queryClient = useQueryClient();
    const [refreshing, setRefreshing] = useState(false);
    const handleRefresh = () => {
        queryClient.invalidateQueries({
            queryKey: queries.orderLog._def,
        });
        setRefreshing(true);
        const timeout = setTimeout(() => {
            setRefreshing(false);
        }, 201);
        return () => clearTimeout(timeout);
    };
    if (orderStatus === 'CANCELLED') {
        return null;
    }
    return (
        <Container fullWidth align='center' spacing={24}>
            <Column fullWidth>
                <ProgressWrapper>
                    <div className={styles.progress}>
                        <div className={styles.progressBar} style={{ width: `calc(${progressWidth}% + 40px)` }}/>
                        <Dot isActive={step === 0}/>
                        <Dot isActive={step === 1}/>
                        <Dot isActive={step === 2}/>
                        <Dot isActive={step === 3}/>
                    </div>
                </ProgressWrapper>
                <Texts>
                    <Text fontWeight='medium'
                        align='center'
                        color={colors.gray6} >
                        {t('progress.paymentCompleted')}
                    </Text>
                    <Text fontWeight='medium'
                        align='center'
                        color={colors.gray6} >
                        {t('progress.orderConfirmed')}
                    </Text>
                    <Text fontWeight='medium'
                        align='center'
                        color={colors.gray6} >
                        {t('progress.preparationCompleted')}
                    </Text>
                    <Text fontWeight='medium'
                        align='center'
                        color={colors.gray6} >
                        {t('progress.pickupCompleted')}
                    </Text>
                </Texts>
            </Column>
            {orderStatus !== 'DONE' && (
                <UpdateButton spacing={8}
                    justify='center'
                    onClick={handleRefresh}
                    css={pressActionEffect} >
                    <RefreshIcon size={20}
                        color={colors.gray7}
                        className={refreshing ? styles.animate : ''} />
                    <Text fontWeight='bold' color={colors.gray7}>
                        {t('info.updateStatus')}
                    </Text>
                </UpdateButton>
            )}
        </Container>
    );
}

const Texts = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    width: 100%;
`;

const Container = styled(Column)`
    padding: 4px 0 0;
`;

const ProgressWrapper = styled.div`
    width: 100%;
    padding: 0 16px;
`;

const UpdateButton = styled(Row)`
    background-color: transparent;
    border: 1px solid ${colors.gray2};
    border-radius: 16px;
    cursor: pointer;
    height: 50px;
    min-width: 100px;
    padding: 0 24px;
    width: fit-content;
`;

export default OrderProgress;
